import { isNotNil } from "client/Misc/utils";
import {
	useState,
	useMemo,
	useEffect,
	useCallback,
	Dispatch,
	SetStateAction,
	useRef,
	RefObject
} from "react";
import { zip } from "client_legacy/ndaw/arrayHelpers";
import { UnreachableCaseError } from "client_legacy/ndaw/errors";

export enum ItemSelectionKind {
	Override = 0,
	Append = 1,
	RangeFromPrevious = 2,
	AddToScope = 3,
	RemoveFromScope = 4,
	SelectAll = 5
}

export enum SelectionScopeKind {
	Override = 0,
	Append = 1
}

export type SelectableItemOptions = {
	isSelected: boolean;
	onSelect(selectionKind: ItemSelectionKind): void;
} | false;

export type MultiSelectableOptions = {
	selectionCount: number;
	areAllSelected: boolean;
	selectAll(): void;
	selectNone(): void;
	startSelection(scopeKind: SelectionScopeKind): void;
	finishSelection(): void;
};

export interface ISelectionHandler<TDataItem> {
	selectionFor(item: TDataItem): SelectableItemOptions;
}

export interface ISingleSelectionHandler<TDataItem> extends ISelectionHandler<TDataItem> {
}

export interface IMultiSelectionHandler<TDataItem> extends ISelectionHandler<TDataItem> {
	multiSelection: MultiSelectableOptions;
}

type SelectionScope<TKey> = {
	originallySelectedKeys: ReadonlyArray<TKey>;
	scopeKind: SelectionScopeKind;
};

type WithSingleSelectSelectionOptions<TDataItem> = {
	isMultiSelect?: false;
	selectedItem: TDataItem|undefined;
	setSelectedItem: Dispatch<TDataItem|undefined>;
};

type WithMultiSelectSelectionOptions<TDataItem> = {
	isMultiSelect: true;
	selectedItems: ReadonlyArray<TDataItem>;
	setSelectedItems: Dispatch<ReadonlyArray<TDataItem>>;
};

type WithMemorizationSelectionOptions<TDataItem> = {
	hasMemorization: true;
	allItems?: ReadonlyArray<TDataItem>;
};

type WithoutMemorizationSelectionOptions = {
	hasMemorization?: false;
};

type SelectionTypeSelectionOptions<TDataItem> =
	| WithSingleSelectSelectionOptions<TDataItem>
	| WithMultiSelectSelectionOptions<TDataItem>;

type MemorizationSelectionOptions<TDataItem> =
	| WithMemorizationSelectionOptions<TDataItem>
	| WithoutMemorizationSelectionOptions;

type SelectionOptions<TDataItem> =
	& SelectionTypeSelectionOptions<TDataItem>
	& MemorizationSelectionOptions<TDataItem>;

type BaseSelectionHandler<TDataItem> = {
	selectionFor(item: TDataItem): SelectableItemOptions;
};

export type SingleSelectionHandler<TDataItem> = BaseSelectionHandler<TDataItem>;

export type MultipleSelectionHandler<TDataItem> = BaseSelectionHandler<TDataItem> & {
	multiSelection: MultiSelectableOptions;
};

type SelectionHandler<TDataItem, TSelectionOptions extends SelectionOptions<TDataItem>> =
	TSelectionOptions extends WithMultiSelectSelectionOptions<TDataItem>
		? MultipleSelectionHandler<TDataItem>
		: SingleSelectionHandler<TDataItem>;

export function useSelection<
	TDataItem,
	TKeyProperty extends StringKeyOf<TDataItem>,
	TSelectionOptions extends SelectionOptions<TDataItem>
>(
	currentlySelectableItems: ReadonlyArray<TDataItem>,
	keyPropertyName: TKeyProperty,
	options: TSelectionOptions
): SelectionHandler<TDataItem, TSelectionOptions> {
	const [selectionScope, setSelectionScope] = useState<SelectionScope<TDataItem[TKeyProperty]>>();

	const {
		setSelectedItem: singleSelectSetSelectedItem,
		selectedItem: singleSelectSelectedItem
	} = (options as WithSingleSelectSelectionOptions<TDataItem>);

	const singleSelectSetSelectedItems = useCallback(
		(value: ReadonlyArray<TDataItem>): void => {
			const items: ReadonlyArray<TDataItem> = value;
			if (items.length > 1) {
				throw new Error("Only one item can be selected in singleSelectionMode");
			}

			singleSelectSetSelectedItem(items[0]);
		},
		[singleSelectSetSelectedItem]
	);

	const singleSelectSelectedItems = useMemo(
		(): TDataItem[] => {
			return singleSelectSelectedItem
				? [singleSelectSelectedItem]
				: [];
		},
		[singleSelectSelectedItem]
	);

	let isMultiSelect: boolean;
	let onSelectedItemsChange: Dispatch<ReadonlyArray<TDataItem>>;
	let selectedItems: ReadonlyArray<TDataItem>;
	const selectedItemsRef: RefObject<readonly TDataItem[]> = useRef<readonly TDataItem[]>(
		null!
	);

	if (options.isMultiSelect === true) {
		isMultiSelect = true;
		selectedItems = (options as WithMultiSelectSelectionOptions<TDataItem>).selectedItems;
		selectedItemsRef.current = selectedItems;
		onSelectedItemsChange = (options as WithMultiSelectSelectionOptions<TDataItem>).setSelectedItems;
	} else {
		isMultiSelect = false;
		selectedItems = singleSelectSelectedItems;
		selectedItemsRef.current = selectedItems;
		onSelectedItemsChange = singleSelectSetSelectedItems;
	}

	const setSelectedItems = useCallback(
		(x: SetStateAction<readonly TDataItem[]>): void => {
			if (typeof x === "function") {
				selectedItemsRef.current = x(selectedItemsRef.current);
				onSelectedItemsChange(selectedItemsRef.current);
			} else {
				selectedItemsRef.current = x;
				onSelectedItemsChange(selectedItemsRef.current);
			}
		},
		[onSelectedItemsChange]
	);

	const itemsForSanitization: ReadonlyArray<TDataItem> | undefined = options.hasMemorization
		? (options as WithMemorizationSelectionOptions<TDataItem>).allItems
		: currentlySelectableItems;

	useEffect(
		() => {
			setSelectedItems(selectedItems => {
				if (!itemsForSanitization) {
					return selectedItems;
				}
				const newSelectedItems: TDataItem[] = selectedItems
					.map(sk => itemsForSanitization.find(ak => ak[keyPropertyName] === sk[keyPropertyName]))
					.filter(isNotNil);

				if (newSelectedItems.length < selectedItems.length) {
					return newSelectedItems;
				}

				const hasChangedItems: boolean = zip(newSelectedItems, selectedItems)
					.some(
						([a, b]) => a !== b
					);
				if (hasChangedItems) {
					return newSelectedItems;
				}

				return selectedItems;
			});
		},
		[
			itemsForSanitization,
			setSelectedItems,
			keyPropertyName
		]
	);

	const multiSelection = useMemo(
		(): MultiSelectableOptions | undefined => {
			if (!isMultiSelect) {
				return undefined;
			}
			return {
				selectionCount: selectedItems.length,
				areAllSelected: selectedItems.length === currentlySelectableItems.length
					&& selectedItems.length > 0,
				selectAll: () => {
					setSelectedItems(currentlySelectableItems);
				},
				selectNone: () => {
					setSelectedItems([]);
				},
				startSelection: (scopeKind: SelectionScopeKind) => {
					setSelectionScope({
						originallySelectedKeys: selectedItems.map(i => i[keyPropertyName]),
						scopeKind: scopeKind
					});
					if (scopeKind === SelectionScopeKind.Override) {
						setSelectedItems([]);
					}
				},
				finishSelection: () => {
					setSelectionScope(undefined);
				}
			};
		},
		[
			isMultiSelect,
			selectedItems,
			currentlySelectableItems,
			setSelectedItems,
			keyPropertyName
		]
	);

	const selectionForStableRefs = useRef<
		{ refs: Map<TDataItem, SelectableItemOptions>; generator: Function; } | null
	>(null);

	const generateOnSelectFor = useCallback(
		(item: TDataItem): ((selectionKind: ItemSelectionKind) => void) | undefined => {
			const isSelectable: boolean = currentlySelectableItems
				.some(k => k[keyPropertyName] === item[keyPropertyName]);

			if (!isSelectable) {
				return undefined;
			}

			return (selectionKind: ItemSelectionKind) => {
				if (!isMultiSelect) {
					setSelectedItems([item]);
					return;
				}
				switch (selectionKind) {
					case ItemSelectionKind.Override:
						setSelectedItems([item]);
						break;
					case ItemSelectionKind.Append:
						setSelectedItems(selectedItems => {
							const copy: TDataItem[] = [...selectedItems];
							const existingKeyIndex: number = copy.findIndex(
								i => i[keyPropertyName] === item[keyPropertyName]
							);

							if (existingKeyIndex >= 0) {
								copy.splice(existingKeyIndex, 1);
							} else {
								copy.push(item);
							}

							return copy;
						});
						break;
					case ItemSelectionKind.RangeFromPrevious:
						setSelectedItems(selectedItems => {
							if (selectedItems.length === 0) {
								return [item];
							}

							const previouslySelectedItem: TDataItem = selectedItems[selectedItems.length - 1];
							const previouslySelectedIndex: number = currentlySelectableItems
								.findIndex(i => i[keyPropertyName] === previouslySelectedItem[keyPropertyName]);

							if (previouslySelectedIndex === -1) {
								return [item];
							}

							const newSelectedIndex: number = currentlySelectableItems
								.findIndex(i => i[keyPropertyName] === item[keyPropertyName]);

							let itemsToSelect: TDataItem[];

							if (previouslySelectedIndex < newSelectedIndex) {
								itemsToSelect = currentlySelectableItems.slice(
									previouslySelectedIndex,
									newSelectedIndex + 1
								);
								itemsToSelect.reverse();
							} else {
								itemsToSelect = currentlySelectableItems.slice(
									newSelectedIndex,
									previouslySelectedIndex + 1
								);
							}

							return itemsToSelect;
						});
						break;
					case ItemSelectionKind.AddToScope:
					case ItemSelectionKind.RemoveFromScope:
						if (!selectionScope) {
							throw new Error("Selection scope is not started, cannot add or remove items into it");
						}

						setSelectedItems(selectedItems => {
							let actions: ChangeItemsInScopeActions;
							const isCurrentlySelected: boolean = selectedItems
								.some(k => k[keyPropertyName] === item[keyPropertyName]);

							switch (selectionScope.scopeKind) {
								case SelectionScopeKind.Override:
									actions = getActionsForOverrideScope(
										selectionKind,
										isCurrentlySelected
									);
									break;
								case SelectionScopeKind.Append:
									const keyWasInOriginalSet: boolean = selectionScope.originallySelectedKeys
										.some(k => k === item[keyPropertyName]);

									actions = getActionsForAppendScope(
										selectionKind,
										isCurrentlySelected,
										keyWasInOriginalSet
									);
									break;
								default:
									throw new UnreachableCaseError(selectionScope.scopeKind);
							}

							if (actions.shouldAddAnItem) {
								return [...selectedItems, item];
							}

							if (actions.shouldRemoveAnItem) {
								return selectedItems.filter(k => k[keyPropertyName] !== item[keyPropertyName]);
							}

							return selectedItems;
						});
						break;
					case ItemSelectionKind.SelectAll:
						setSelectedItems(currentlySelectableItems);

						break;
					default:
						throw new UnreachableCaseError(selectionKind);
				}
			};
		},
		[
			isMultiSelect,
			currentlySelectableItems,
			keyPropertyName,
			selectionScope,
			setSelectedItems
		]
	);

	const selectionFor = useCallback(
		(item: TDataItem): SelectableItemOptions => {
			if (selectionForStableRefs.current === null
				|| selectionForStableRefs.current.generator !== generateOnSelectFor
			) {
				selectionForStableRefs.current = {
					generator: generateOnSelectFor,
					refs: new Map()
				};
			}

			const {
				refs
			} = selectionForStableRefs.current;

			const isSelected: boolean = selectedItems
				.some(k => k[keyPropertyName] === item[keyPropertyName]);

			const onSelect: ((selectionKind: ItemSelectionKind) => void) | undefined = generateOnSelectFor(
				item
			);

			let result: SelectableItemOptions;
			if (refs.has(item)) {
				result = refs.get(item)!;

				if (result === false) {
					if (!onSelect) {
						return result;
					}
				} else {
					if (isNotNil(onSelect) && result.isSelected === isSelected) {
						return result;
					}
				}
			}

			result = isNotNil(onSelect)
				? {
					isSelected,
					onSelect
				}
				: false;

			refs.set(item, result);
			return result;
		},
		[
			generateOnSelectFor,
			keyPropertyName,
			selectedItems
		]
	);

	return {
		multiSelection,
		selectionFor
	} as unknown as SelectionHandler<TDataItem, TSelectionOptions>;
}

type ChangeItemsInScopeActions = {
	shouldAddAnItem: boolean;
	shouldRemoveAnItem: boolean;
};

function getActionsForOverrideScope(
	scopeSelectionKind: ItemSelectionKind.AddToScope | ItemSelectionKind.RemoveFromScope,
	isCurrentlySelected: boolean
): ChangeItemsInScopeActions {
	switch (scopeSelectionKind) {
		case ItemSelectionKind.AddToScope:
			return {
				shouldAddAnItem: !isCurrentlySelected,
				shouldRemoveAnItem: false,
			};
		case ItemSelectionKind.RemoveFromScope:
			return {
				shouldAddAnItem: false,
				shouldRemoveAnItem: isCurrentlySelected
			};
		default:
			throw new UnreachableCaseError(scopeSelectionKind);
	}
}

function getActionsForAppendScope(
	scopeSelectionKind: ItemSelectionKind.AddToScope|ItemSelectionKind.RemoveFromScope,
	isCurrentlySelected: boolean,
	keyWasInOriginalSet: boolean
): ChangeItemsInScopeActions {
	switch (scopeSelectionKind) {
		case ItemSelectionKind.AddToScope:
			return {
				shouldAddAnItem: !keyWasInOriginalSet && !isCurrentlySelected,
				shouldRemoveAnItem: keyWasInOriginalSet && isCurrentlySelected
			};
		case ItemSelectionKind.RemoveFromScope:
			return {
				shouldAddAnItem: keyWasInOriginalSet && !isCurrentlySelected,
				shouldRemoveAnItem: !keyWasInOriginalSet && isCurrentlySelected
			};
		default:
			throw new UnreachableCaseError(scopeSelectionKind);
	}
}
